import { AutoCompleteTextField } from '@modules/common/components/AutoCompleteTextField';
import {
  getFilterValueFromType,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { Option } from '@modules/queryBuilder/types';
import { Autocomplete, Box } from '@mui/material';
import { useState } from 'react';
import { OperatorContainsAnyOf } from 'v2/core/services/queryBuilder/operators';
import { NormalisedFilter } from 'v2/querybuilder/types/querystore';
import { QueryFilterNLSGroupHeader } from '../QueryFilterNLSGroupHeader';
import { QueryFilterNLSNoInputOption } from '../QueryFilterNLSNoInputOption';
import { QueryFilterNLSSearchTermOption } from '../QueryFilterNLSSearchTermOption';
import { QueryFilterNLSSelectedOption } from '../QueryFilterNLSSelectedOption';

interface QueryFilterNLSAutoCompleteProps {
  filter: NormalisedFilter;
  isMenuOpen: boolean;
}

const TO_FILTER_OPTION_ID = 'ADD_NATURAL_LANGUAGE_SEARCH';

export const QueryFilterNLSAutoComplete = ({
  filter,
  isMenuOpen,
}: QueryFilterNLSAutoCompleteProps) => {
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedNLSOptions =
    filter.value.type === 'string[]'
      ? filter.value.values.map((value) => ({
          id: value.value,
          displayName: value.value,
          group: 'Selected text search terms',
        }))
      : [];

  const optionLabel =
    filter.operator === OperatorContainsAnyOf
      ? 'Find items containing'
      : 'Find items with';

  const options = [
    {
      id: TO_FILTER_OPTION_ID,
      displayName: `${optionLabel} `,
      group: '',
    },
    ...selectedNLSOptions,
  ];

  const onValueChange = (value: (string | Option)[] | null) => {
    if (!value) {
      return;
    }

    const lastItem = value.slice(-1).pop();
    const newValue = (() => {
      if (typeof lastItem === 'string') {
        return lastItem;
      }
      if (lastItem?.id === TO_FILTER_OPTION_ID) {
        return searchTerm;
      }
      return lastItem?.id;
    })();

    if (!newValue) {
      return;
    }

    const existingNLSvalues =
      filter.value.type === 'string[]' &&
      new Set(filter.value.values.map((value) => value.value));

    if (!existingNLSvalues || existingNLSvalues.has(newValue)) {
      return;
    }

    const updatedNodes = upsertQueryFilterNode({
      node: {
        ...filter,
        value: getFilterValueFromType({
          value: [...existingNLSvalues, newValue],
          type: filter.value.type,
        }),
      },
      nodesByID: nodes,
      parentID: filter.parent,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });
  };

  return (
    <Autocomplete
      open={isMenuOpen}
      freeSolo
      fullWidth
      multiple
      autoHighlight
      options={options}
      popupIcon={false}
      disablePortal={true}
      disableCloseOnSelect
      renderInput={(params) => (
        <AutoCompleteTextField placeholder="Type something..." {...params} />
      )}
      groupBy={(option) => option.group}
      onInputChange={(event, value, reason) => {
        if (reason === 'input') {
          setSearchTerm(value);
        }
      }}
      renderOption={(props, option, { inputValue, selected }) => {
        if (option.id === TO_FILTER_OPTION_ID && !inputValue) {
          return <QueryFilterNLSNoInputOption key={option.id} />;
        }

        if (option.id === TO_FILTER_OPTION_ID) {
          return (
            <QueryFilterNLSSearchTermOption
              option={option}
              inputValue={inputValue}
              selected={selected}
              baseProps={props}
              key={option.id}
            />
          );
        }
        return (
          <QueryFilterNLSSelectedOption
            key={option.id}
            option={option}
            filter={filter}
          />
        );
      }}
      renderGroup={(params) => (
        <Box component="li" key={params.key}>
          <QueryFilterNLSGroupHeader header={params.group} />
          {params.children}
        </Box>
      )}
      componentsProps={{
        paper: {
          elevation: 8,
          sx: { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
        },
      }}
      onChange={(eve, value) => {
        onValueChange(value ?? null);
      }}
      filterOptions={(options) => options}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.displayName
      }
    />
  );
};
